// frontend/src/components/Navbar.js
import React, { useContext, useState, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Badge,
  Box,
  IconButton,
  useMediaQuery,
  useTheme,
  Menu,
  MenuItem,
  Divider,
  Fade,
  Slide,
  Chip,
  Avatar
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import NotificationsIcon from '@mui/icons-material/Notifications';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import HomeIcon from '@mui/icons-material/Home';
import InfoIcon from '@mui/icons-material/Info';
import ExploreIcon from '@mui/icons-material/Explore';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import SchoolIcon from '@mui/icons-material/School';
import QuizIcon from '@mui/icons-material/Quiz';
import AssignmentIcon from '@mui/icons-material/Assignment';
import GradeIcon from '@mui/icons-material/Grade';
import LoginIcon from '@mui/icons-material/Login';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import LogoutIcon from '@mui/icons-material/Logout';
import { Link, useNavigate } from 'react-router-dom';
import { AuthContext } from '../contexts/AuthContext';
import { NotificationContext } from '../contexts/NotificationContext';
import { styled, keyframes } from '@mui/material/styles';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import axios from 'axios';



// Register GSAP plugins
gsap.registerPlugin(ScrollTrigger);

// Animations
const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-3px); }
`;

const pulse = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.05); }
`;

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;


// Modern styled components with glassmorphism and advanced animations
const StyledAppBar = styled(AppBar)(({ theme }) => ({
  background: 'rgba(255, 255, 255, 0.95)',
  backdropFilter: 'blur(20px)',
  WebkitBackdropFilter: 'blur(20px)',
  borderRadius: '0',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
  margin: '0',
  width: '100%',
  color: theme.palette.text.primary,
  border: 'none',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '1px',
    background: 'linear-gradient(90deg, transparent, rgba(99, 102, 241, 0.5), transparent)',
  },
}));

const LogoBox = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  textDecoration: 'none',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  position: 'relative',
  '&:hover': {
    transform: 'scale(1.05) translateY(-2px)',
    '& .logo-image': {
      animation: `${float} 2s ease-in-out infinite`,
    },
  }
});

const LogoImage = styled('img')({
  height: 48,
  marginRight: 16,
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  filter: 'drop-shadow(0 4px 8px rgba(99, 102, 241, 0.2))',
  '&:hover': {
    transform: 'rotate(-8deg) scale(1.1)',
    filter: 'drop-shadow(0 6px 12px rgba(99, 102, 241, 0.3))',
  }
});

const LogoText = styled(Typography)({
  fontFamily: '"Inter", "Montserrat", sans-serif',
  fontWeight: 800,
  fontSize: '1.5rem',
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%)',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundSize: '200% 200%',
  animation: `${shimmer} 3s ease-in-out infinite`,
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  textShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
});

const NavButton = styled(Button)(({ theme }) => ({
  color: '#374151',
  textTransform: 'none',
  fontWeight: 600,
  fontSize: '0.95rem',
  margin: theme.spacing(0, 0.5),
  padding: theme.spacing(1.2, 2.5),
  borderRadius: '16px',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  position: 'relative',
  overflow: 'hidden',
  background: 'rgba(255, 255, 255, 0.1)',
  backdropFilter: 'blur(10px)',
  border: '1px solid rgba(255, 255, 255, 0.2)',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    height: '100%',
    width: 0,
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    transition: 'width 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
    zIndex: -1,
  },
  '&:hover': {
    color: 'white',
    transform: 'translateY(-3px) scale(1.05)',
    boxShadow: '0 10px 25px rgba(102, 126, 234, 0.3)',
    background: 'rgba(102, 126, 234, 0.1)',
    '&::before': {
      width: '100%',
    },
  },
  '&:active': {
    transform: 'translateY(-1px) scale(1.02)',
  }
}));

const ProfileButton = styled(Button)({
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  color: 'white',
  textTransform: 'none',
  fontWeight: 600,
  borderRadius: '20px',
  padding: '10px 20px',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  boxShadow: '0 8px 20px rgba(102, 126, 234, 0.3)',
  border: 'none',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: '-100%',
    width: '100%',
    height: '100%',
    background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent)',
    transition: 'left 0.5s',
  },
  '&:hover': {
    transform: 'translateY(-3px) scale(1.05)',
    boxShadow: '0 15px 30px rgba(102, 126, 234, 0.4)',
    '&::before': {
      left: '100%',
    },
  },
  '&:active': {
    transform: 'translateY(-1px) scale(1.02)',
  }
});

const RegisterButton = styled(Button)({
  background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
  color: 'white',
  textTransform: 'none',
  fontWeight: 600,
  borderRadius: '20px',
  padding: '10px 24px',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  boxShadow: '0 8px 20px rgba(240, 147, 251, 0.3)',
  border: 'none',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: '-100%',
    width: '100%',
    height: '100%',
    background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent)',
    transition: 'left 0.5s',
  },
  '&:hover': {
    transform: 'translateY(-3px) scale(1.05)',
    boxShadow: '0 15px 30px rgba(240, 147, 251, 0.4)',
    background: 'linear-gradient(135deg, #f5576c 0%, #f093fb 100%)',
    '&::before': {
      left: '100%',
    },
  },
  '&:active': {
    transform: 'translateY(-1px) scale(1.02)',
  }
});

const MobileMenuContainer = styled(Box)(({ theme }) => ({
  background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.9) 100%)',
  backdropFilter: 'blur(20px)',
  WebkitBackdropFilter: 'blur(20px)',
  padding: theme.spacing(3),
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1.5),
  borderRadius: '0 0 24px 24px',
  margin: '0 20px',
  boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
  border: '1px solid rgba(255, 255, 255, 0.3)',
  borderTop: 'none',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '1px',
    background: 'linear-gradient(90deg, transparent, rgba(102, 126, 234, 0.5), transparent)',
  },
}));

const NotificationBadge = styled(Badge)({
  '& .MuiBadge-badge': {
    background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a24 100%)',
    color: 'white',
    fontWeight: 'bold',
    fontSize: '0.75rem',
    animation: `${pulse} 2s ease-in-out infinite`,
    boxShadow: '0 2px 8px rgba(255, 107, 107, 0.3)',
  }
});

const MobileNavButton = styled(Button)(({ theme }) => ({
  color: '#374151',
  textTransform: 'none',
  fontWeight: 600,
  fontSize: '1rem',
  padding: theme.spacing(1.5, 2),
  borderRadius: '16px',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  background: 'rgba(255, 255, 255, 0.1)',
  backdropFilter: 'blur(10px)',
  border: '1px solid rgba(255, 255, 255, 0.2)',
  justifyContent: 'flex-start',
  '&:hover': {
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
    transform: 'translateX(8px) scale(1.02)',
    boxShadow: '0 8px 20px rgba(102, 126, 234, 0.3)',
  },
}));

const MenuIconButton = styled(IconButton)({
  color: '#374151',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  borderRadius: '12px',
  padding: '8px',
  '&:hover': {
    backgroundColor: 'rgba(102, 126, 234, 0.1)',
    color: '#667eea',
    transform: 'scale(1.1) rotate(90deg)',
  }
});

const Navbar = () => {
  const { token, username, setAuth } = useContext(AuthContext);
  const { newNotification } = useContext(NotificationContext);
  const navigate = useNavigate();
  const isLoggedIn = !!token;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  const timeout = (fn, ms) => setTimeout(fn, ms);
  // State to control mobile menu visibility
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const open = Boolean(anchorEl);

  // Handle scroll effect with GSAP
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };

    // GSAP scroll-triggered navbar animation
    gsap.to('.navbar-container', {
      backgroundColor: 'rgba(255, 255, 255, 0.95)',
      backdropFilter: 'blur(20px)',
      height: '80px',
      duration: 0.5,
      scrollTrigger: {
        trigger: '.navbar-container',
        start: 'top -10%',
        end: 'top -11%',
        scrub: 1
      }
    });

    // Animate navbar items on scroll
    // gsap.fromTo('.nav-item', 
    //   {
    //     y: -20,
    //     opacity: 0
    //   },
    //   {
    //     y: 0,
    //     opacity: 1,
    //     duration: 0.6,
    //     stagger: 0.1,
    //     ease: 'power2.out',
    //     scrollTrigger: {
    //       trigger: '.navbar-container',
    //       start: 'top 80%',
    //       end: 'top 60%',
    //       toggleActions: 'play none none reverse'
    //     }
    //   }
    // );

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
    };
  }, []);

  const toggleMobileMenu = () => setMobileMenuOpen((prev) => !prev);

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    try {
      const userId = localStorage.getItem('userId');

      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      const response = await axios.post(`${apiUrl}/api/auth/logout`, { userId });

      if (response.status === 200) {
        setAuth({ token: null, role: null, username: null });
        localStorage.clear();
        navigate('/login');
        handleMenuClose();
        setMobileMenuOpen(false);
      } else {
        setSnackbar({ open: true, message: 'Logout failed: ' + response.data.message, severity: 'error' });
      }
    } catch (error) {
      console.error('Error during logout:', error);
      setSnackbar({ open: true, message: 'Error during logout. Please try again.', severity: 'error' });
    }
  };

  const closeSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Navigation buttons for desktop with icons
  const desktopNavButtons = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <NavButton component={Link} to="/" startIcon={<HomeIcon />}>
        Home
      </NavButton>
      <NavButton component={Link} to="/about" startIcon={<InfoIcon />}>
        About
      </NavButton>
      <NavButton component={Link} to="/dashboard" startIcon={<ExploreIcon />}>
        Explore
      </NavButton>

      {isLoggedIn && username === 'admin' && (
        <NavButton component={Link} to="/admin" startIcon={<AdminPanelSettingsIcon />}>
          Admin
        </NavButton>
      )}

      {isLoggedIn && localStorage.getItem('role') === 'teacher' && (
        <>
          <NavButton component={Link} to="/performance-dashboard" startIcon={<SchoolIcon />}>
            Performance
          </NavButton>
          {/* <NavButton component={Link} to="/teacher-quiz-create" startIcon={<QuizIcon />}>
            Create Quiz
          </NavButton> */}
          {/* <NavButton component={Link} to="/teacher-assignment-create" startIcon={<AssignmentIcon />}>
            Assignments
          </NavButton>
          <NavButton component={Link} to="/grade-submissions" startIcon={<GradeIcon />}>
            Grading
          </NavButton> */}
        </>
      )}
    </Box>
  );

  // Auth buttons for desktop
  const desktopAuthButtons = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
      {isLoggedIn && (
        <IconButton
          onClick={() => navigate('/notifications')}
          sx={{
            color: '#374151',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            borderRadius: '12px',
            padding: '8px',
            '&:hover': {
              color: '#667eea',
              backgroundColor: 'rgba(102, 126, 234, 0.1)',
              transform: 'scale(1.1)'
            }
          }}
        >
          <NotificationBadge color="error" variant="dot" invisible={!newNotification}>
            <NotificationsIcon />
          </NotificationBadge>
        </IconButton>
      )}

      {!isLoggedIn ? (
        <>
          <NavButton component={Link} to="/login" startIcon={<LoginIcon />}>
            Login
          </NavButton>
          <RegisterButton component={Link} to="/register" startIcon={<PersonAddIcon />}>
            Register
          </RegisterButton>
        </>
      ) : (
        <>
          <ProfileButton onClick={handleProfileMenuOpen} startIcon={<AccountCircleIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Avatar sx={{ width: 24, height: 24, fontSize: '0.75rem', background: 'rgba(255, 255, 255, 0.2)' }}>
                {username?.charAt(0)?.toUpperCase()}
              </Avatar>
              {username}
            </Box>
          </ProfileButton>
          <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={handleMenuClose}
            PaperProps={{
              elevation: 0,
              sx: {
                overflow: 'visible',
                filter: 'drop-shadow(0px 8px 32px rgba(0,0,0,0.12))',
                mt: 1.5,
                borderRadius: '16px',
                background: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                minWidth: 200,
                '&:before': {
                  content: '""',
                  display: 'block',
                  position: 'absolute',
                  top: 0,
                  right: 20,
                  width: 12,
                  height: 12,
                  bgcolor: 'rgba(255, 255, 255, 0.95)',
                  transform: 'translateY(-50%) rotate(45deg)',
                  zIndex: 0,
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                  borderBottom: 'none',
                  borderRight: 'none',
                },
              },
            }}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          >
            <MenuItem
              onClick={() => { navigate('/profile'); handleMenuClose(); }}
              sx={{
                borderRadius: '8px',
                margin: '4px 8px',
                '&:hover': {
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white'
                }
              }}
            >
              <AccountCircleIcon sx={{ mr: 1 }} />
              Profile
            </MenuItem>
            <Divider sx={{ my: 1 }} />
            <MenuItem
              onClick={handleLogout}
              sx={{
                borderRadius: '8px',
                margin: '4px 8px',
                '&:hover': {
                  background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a24 100%)',
                  color: 'white'
                }
              }}
            >
              <LogoutIcon sx={{ mr: 1 }} />
              Logout
            </MenuItem>
          </Menu>
        </>
      )}
    </Box>
  );

  // Mobile navigation menu with enhanced design
  const mobileNavMenu = (
    <Slide direction="down" in={mobileMenuOpen} mountOnEnter unmountOnExit>
      <MobileMenuContainer>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#374151' }}>
            Menu
          </Typography>
          <IconButton onClick={toggleMobileMenu} sx={{ color: '#374151' }}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <MobileNavButton component={Link} to="/" fullWidth onClick={toggleMobileMenu} startIcon={<HomeIcon />}>
            Home
          </MobileNavButton>
          <MobileNavButton component={Link} to="/about" fullWidth onClick={toggleMobileMenu} startIcon={<InfoIcon />}>
            About
          </MobileNavButton>
          <MobileNavButton component={Link} to="/dashboard" fullWidth onClick={toggleMobileMenu} startIcon={<ExploreIcon />}>
            Explore
          </MobileNavButton>

          {isLoggedIn && (
            <MobileNavButton component={Link} to="/profile" fullWidth onClick={toggleMobileMenu} startIcon={<AccountCircleIcon />}>
              Profile
            </MobileNavButton>
          )}

          {isLoggedIn && username === 'admin' && (
            <MobileNavButton component={Link} to="/admin" fullWidth onClick={toggleMobileMenu} startIcon={<AdminPanelSettingsIcon />}>
              Admin Panel
            </MobileNavButton>
          )}

          {isLoggedIn && localStorage.getItem('role') === 'teacher' && (
            <>
              <MobileNavButton component={Link} to="/performance-dashboard" fullWidth onClick={toggleMobileMenu} startIcon={<SchoolIcon />}>
                Performance
              </MobileNavButton>
              <MobileNavButton component={Link} to="/teacher-quiz-create" fullWidth onClick={toggleMobileMenu} startIcon={<QuizIcon />}>
                Create Quiz
              </MobileNavButton>
              <MobileNavButton component={Link} to="/teacher-assignment-create" fullWidth onClick={toggleMobileMenu} startIcon={<AssignmentIcon />}>
                Assignments
              </MobileNavButton>
              <MobileNavButton component={Link} to="/grade-submissions" fullWidth onClick={toggleMobileMenu} startIcon={<GradeIcon />}>
                Grading
              </MobileNavButton>
            </>
          )}

          {isLoggedIn && (
            <MobileNavButton onClick={() => { navigate('/notifications'); toggleMobileMenu(); }} fullWidth startIcon={<NotificationsIcon />}>
              Notifications
              {newNotification && (
                <Chip
                  label="New"
                  size="small"
                  sx={{
                    ml: 1,
                    background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a24 100%)',
                    color: 'white',
                    fontSize: '0.7rem',
                    height: 20
                  }}
                />
              )}
            </MobileNavButton>
          )}

          {/* Snackbar Notification */}
          {snackbar.open && (
            <div className={`snackbar ${snackbar.severity}`}>
              <span>{snackbar.message}</span>
              <button className="snackbar-close" onClick={closeSnackbar}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z" />
                </svg>
              </button>
            </div>
          )}
        </Box>

        <Divider sx={{ my: 2 }} />

        {!isLoggedIn ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <MobileNavButton component={Link} to="/login" fullWidth onClick={toggleMobileMenu} startIcon={<LoginIcon />}>
              Login
            </MobileNavButton>
            <RegisterButton component={Link} to="/register" fullWidth onClick={toggleMobileMenu} startIcon={<PersonAddIcon />}>
              Register
            </RegisterButton>
          </Box>
        ) : (
          <MobileNavButton onClick={handleLogout} fullWidth startIcon={<LogoutIcon />}>
            Logout
          </MobileNavButton>
        )}
      </MobileMenuContainer>
    </Slide>
  );



  return (
    <>
      <StyledAppBar
        className="navbar-container"
        position="fixed"
        sx={{
          transform: scrolled ? 'translateY(-10px)' : 'translateY(0)',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          boxShadow: scrolled
            ? '0 25px 50px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(255, 255, 255, 0.2)'
            : '0 20px 40px rgba(0, 0, 0, 0.1), 0 0 0 1px rgba(255, 255, 255, 0.2)'
        }}
      >
        <Toolbar sx={{
          justifyContent: 'space-between',
          padding: '8px 20px',
          minHeight: '64px !important',
          maxWidth: '1200px',
          margin: '0 auto',
          width: '100%'
        }}>
          {/* Logo section with enhanced animations */}
          <Fade in timeout={800}>
            <LogoBox component={Link} to="/">
              <LogoImage
                src="/exam.png"
                alt="SSPACE Assist Logo"
                className="logo-image"
                sx={{
                  height: scrolled ? 44 : 48,
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                }}
              />
              <LogoText
                variant="h6"
                sx={{
                  fontSize: scrolled ? '1.3rem' : '1.5rem',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                }}
              >
                SSPACE Assist
              </LogoText>
            </LogoBox>
          </Fade>

          {/* Desktop Navigation with staggered animation */}
          <Box sx={{
            display: { xs: 'none', md: 'flex' },
            alignItems: 'center',
            gap: 1
          }}>
            <Fade in timeout={1000}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {desktopNavButtons}
              </Box>
            </Fade>
            <Fade in timeout={1200}>
              <Box>
                {desktopAuthButtons}
              </Box>
            </Fade>
          </Box>

          {/* Mobile menu button with enhanced animation */}
          <MenuIconButton
            sx={{
              display: { xs: 'flex', md: 'none' },
              transform: mobileMenuOpen ? 'rotate(90deg)' : 'rotate(0deg)',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
            }}
            onClick={toggleMobileMenu}
          >
            {mobileMenuOpen ? <CloseIcon /> : <MenuIcon />}
          </MenuIconButton>
        </Toolbar>
      </StyledAppBar>

      {/* Dummy Toolbar to offset page content below fixed Navbar */}
      <Toolbar sx={{ minHeight: '0px !important' }} />

      {/* Mobile Navigation Menu with enhanced animations */}
      {isMobile && mobileNavMenu}
    </>
  );
};

export default Navbar;